/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving drug dispensations.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugDispensationsState {
    drugDispensations: Dtos.DrugDispensation[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugDispensationsState {
    drugDispensations: IInitialDrugDispensationsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugDispensationsInitialState: IInitialDrugDispensationsState = {
    drugDispensations: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugDispensationsSelector: TypedUseSelectorHook<IDrugDispensationsState> = useSelector;

export const DrugDispensationsSelectors = {
    selectDrugDispensations: (state: IDrugDispensationsState) => state.drugDispensations.drugDispensations,
    selectLoadState: (state: IDrugDispensationsState) => state.drugDispensations.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugDispensationsReducer extends ImmerReducer<IInitialDrugDispensationsState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByPatientId(patientId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugDispensations?: Dtos.DrugDispensation[]) {
        this.draftState.drugDispensations = drugDispensations ? drugDispensations : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugDispensations = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugDispensationsInitialState };
    }
};

export const DrugDispensationsActions = createActionCreators(DrugDispensationsReducer);
const reducer = createReducerFunction(DrugDispensationsReducer, DrugDispensationsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugDispensationsApi = {
    load: () => {
        return client.get(new Dtos.GetDrugDispensations());
    },
    loadByPatientId: (patientId: number) => {
        return client.get(new Dtos.GetDrugDispensationByPatientId({
            patientId
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugDispensationsLogic = {
    load: createLogic<IDrugDispensationsState, {}, undefined, string, ReturnType<typeof DrugDispensationsActions.load>>({
        type: DrugDispensationsActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await DrugDispensationsApi.load();

                dispatch(DrugDispensationsActions.loadSuccess(
                    response.drugDispensations
                ));
            }
            catch (error: any) {
                dispatch(DrugDispensationsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByPatientId: createLogic<IDrugDispensationsState, {}, undefined, string, ReturnType<typeof DrugDispensationsActions.loadByPatientId>>({
        type: DrugDispensationsActions.loadByPatientId.type,
        process: async ({ action }, dispatch, done) => {
            const patientId = action.payload;

            try {
                const response = await DrugDispensationsApi.loadByPatientId(patientId);

                dispatch(DrugDispensationsActions.loadSuccess(
                    response.drugDispensations
                ));
            }
            catch (error: any) {
                dispatch(DrugDispensationsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
};

const logics = [
    DrugDispensationsLogic.load,
    DrugDispensationsLogic.loadByPatientId
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugDispensations = (): [Dtos.DrugDispensation[] | null, IRequestState<Dtos.ResponseStatus>, typeof DrugDispensationsActions] => {
    const drugDispensations = useSelector(DrugDispensationsSelectors.selectDrugDispensations);
    const loadState = useSelector(DrugDispensationsSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugDispensationsActions>(() => {
        return bindActionCreators(DrugDispensationsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugDispensationsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugDispensations,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugDispensations', reducer as any, logics as any);